import { useEffect } from "react";
import axios from "axios";
import {
  readDraft,
  writeDraft,
  clearDraft,
  isEmptyDraft,
  clearDraftsOnSuccessfulSave,
} from "./draft_storage";
import {
  collectSnapshot,
  applyDraft,
  blankForm,
  safeCall,
  safeCallAsync,
} from "./draft_engine";

const SKIP_POST_URL = /check_for|duplicates|get_all|permission/i;
const SAVE_POST_URL = /\/(add_[^/?#]+|change_[^/?#]+|update_[^/?#]+|edit_[^/?#]+|create[^/?#]*)\/?(\?|#|$)/i;

let saveTimer = null;
let observerTimer = null;
let restoring = false;
let ignorePop = false;
let lastOpenIdentity = null;
let pushedHistory = false;
let hadOpenModal = false;
let restoreGen = 0;
const openIdentities = new Set();
const dirtyIdentities = new Set();
const restoreInFlight = new WeakSet();
const justSaved = new Set();

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function visibleModals() {
  return safeCall(() => {
    return [...document.querySelectorAll(".modal.show")].filter((el) => {
      const style = window.getComputedStyle(el);
      return style.display !== "none" && style.visibility !== "hidden";
    });
  }, []);
}

function topModal() {
  const list = visibleModals();
  return list[list.length - 1] || null;
}

function modalIdentity(modal) {
  return safeCall(() => {
    if (!modal) return "";
    const title = (modal.querySelector(".modal-title")?.textContent || "")
      .replace(/\s+/g, " ")
      .trim();
    return window.location.pathname + "::" + (title || "untitled");
  }, "");
}

function showBanner(modal, identity) {
  safeCall(() => {
    if (!modal || modal.querySelector(".gp-draft-banner")) return;
    const body = modal.querySelector(".modal-body");
    if (!body) return;
    const banner = document.createElement("div");
    banner.className = "gp-draft-banner";
    banner.innerHTML =
      '<span>Unsaved form restored.</span> <button type="button" class="gp-draft-discard">Discard</button>';
    body.insertBefore(banner, body.firstChild);
    banner.querySelector(".gp-draft-discard").addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      clearDraft(identity);
      justSaved.add(identity);
      restoring = true;
      restoreGen += 1;
      const closer = modal.querySelector(".close") || modal.querySelector('[aria-label="Close"]');
      if (closer) closer.click();
      setTimeout(() => {
        restoring = false;
      }, 200);
    });
  });
}

function injectStyles() {
  safeCall(() => {
    if (document.getElementById("gp-draft-styles")) return;
    const style = document.createElement("style");
    style.id = "gp-draft-styles";
    style.textContent = `
      .gp-draft-banner {
        background: #fff6ee;
        border: 1px solid #f3702d;
        color: #333;
        padding: 8px 12px;
        margin-bottom: 12px;
        border-radius: 4px;
        font-size: 13px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
      }
      .gp-draft-discard {
        background: transparent;
        border: 1px solid #f3702d;
        color: #f3702d;
        border-radius: 4px;
        padding: 3px 10px;
        cursor: pointer;
      }
      .gp-draft-discard:hover {
        background: #f3702d;
        color: #fff;
      }
    `;
    document.head.appendChild(style);
  });
}

function saveTopModal() {
  safeCall(() => {
    if (restoring) return;
    const modal = topModal();
    if (!modal) return;
    const identity = modalIdentity(modal);
    if (!identity) return;
    if (!dirtyIdentities.has(identity)) return;
    const snap = collectSnapshot(modal);
    if (isEmptyDraft(snap)) return;
    writeDraft(identity, snap);
    lastOpenIdentity = identity;
  });
}

function scheduleSave() {
  clearTimeout(saveTimer);
  saveTimer = setTimeout(saveTopModal, 250);
}

function markDirtyFromEvent(e) {
  safeCall(() => {
    if (restoring) return;
    const option = e.target && e.target.closest && e.target.closest('[role="option"]');
    const modal =
      (e.target && e.target.closest && e.target.closest(".modal.show")) || (option ? topModal() : null);
    if (!modal) return;
    dirtyIdentities.add(modalIdentity(modal));
    scheduleSave();
  });
}

function dismissTopModal() {
  safeCall(() => {
    const modal = topModal();
    if (!modal) return;
    saveTopModal();
    const closer =
      modal.querySelector(".close") ||
      modal.querySelector('[aria-label="Close"]') ||
      modal.querySelector(".modal-header button");
    if (closer) closer.click();
  });
}

async function restoreModal(modal) {
  await safeCallAsync(async () => {
    if (!modal || restoreInFlight.has(modal)) return;
    const identity = modalIdentity(modal);
    const draft = readDraft(identity);
    if (!draft || isEmptyDraft(draft)) {
      if (justSaved.has(identity)) {
        justSaved.delete(identity);
        restoring = true;
        try {
          blankForm(modal);
        } finally {
          restoring = false;
        }
      }
      return;
    }

    restoreInFlight.add(modal);
    const gen = ++restoreGen;
    restoring = true;
    const toggledChecks = new Set();
    try {
      showBanner(modal, identity);
      const delays = [0, 300, 800, 1600, 2800, 4500];
      let last = 0;
      for (const ms of delays) {
        if (ms > last) await sleep(ms - last);
        last = ms;
        if (gen !== restoreGen) return;
        if (topModal() !== modal) return;
        await applyDraft(modal, draft, toggledChecks);
      }
    } finally {
      if (gen === restoreGen) restoring = false;
      restoreInFlight.delete(modal);
    }
  });
}

function onModalOpened(modal) {
  safeCall(() => {
    const identity = modalIdentity(modal);
    lastOpenIdentity = identity;
    if (openIdentities.has(identity)) return;
    openIdentities.add(identity);

    if (!pushedHistory) {
      try {
        window.history.pushState({ gpModalDraft: true, identity }, "", window.location.href);
        pushedHistory = true;
      } catch (e) {}
    }
    restoreModal(modal);
  });
}

function onAllModalsClosed() {
  safeCall(() => {
    openIdentities.clear();
    restoreGen += 1;
    restoring = false;
    if (pushedHistory && window.history.state && window.history.state.gpModalDraft) {
      ignorePop = true;
      pushedHistory = false;
      window.history.back();
      setTimeout(() => {
        ignorePop = false;
      }, 80);
    }
  });
}

function syncOpenModals() {
  safeCall(() => {
    const modals = visibleModals();
    const current = new Set(modals.map(modalIdentity));

    modals.forEach((modal) => {
      const identity = modalIdentity(modal);
      if (!openIdentities.has(identity)) {
        saveTopModal();
        onModalOpened(modal);
      }
    });

    [...openIdentities].forEach((identity) => {
      if (!current.has(identity)) openIdentities.delete(identity);
    });

    if (modals.length) {
      hadOpenModal = true;
      lastOpenIdentity = modalIdentity(modals[modals.length - 1]);
    } else if (hadOpenModal) {
      hadOpenModal = false;
      onAllModalsClosed();
    }
  });
}

function shouldClearFromUrl(url) {
  if (!url) return false;
  if (SKIP_POST_URL.test(url)) return false;
  return SAVE_POST_URL.test(url);
}

function ModalDraftManager() {
  useEffect(() => {
    injectStyles();

    const observer = new MutationObserver(() => {
      clearTimeout(observerTimer);
      observerTimer = setTimeout(() => {
        safeCall(syncOpenModals);
      }, 40);
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "style"],
    });

    const onInput = (e) => markDirtyFromEvent(e);
    const flushSave = () => {
      if (restoring) return;
      if (topModal()) saveTopModal();
    };
    document.addEventListener("input", onInput, true);
    document.addEventListener("change", onInput, true);
    document.addEventListener("click", onInput, true);
    document.addEventListener("mousedown", flushSave, true);

    const onPop = () => {
      safeCall(() => {
        if (ignorePop) return;
        if (topModal()) {
          dismissTopModal();
        }
        pushedHistory = false;
      });
    };
    window.addEventListener("popstate", onPop);

    const persistNow = () => saveTopModal();
    window.addEventListener("pagehide", persistNow);
    window.addEventListener("beforeunload", persistNow);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") persistNow();
    });

    const interceptor = axios.interceptors.response.use(
      (res) => {
        try {
          const method = (res.config?.method || "").toLowerCase();
          const url = res.config?.url || "";
          if (method === "post" && res.status >= 200 && res.status < 300 && shouldClearFromUrl(url)) {
            const ids = [lastOpenIdentity, modalIdentity(topModal()), ...openIdentities];
            ids.forEach((id) => {
              if (id) justSaved.add(id);
            });
            clearDraftsOnSuccessfulSave(window.location.pathname, ids);
            dirtyIdentities.clear();
          }
        } catch (e) {}
        return res;
      },
      (err) => Promise.reject(err)
    );

    return () => {
      observer.disconnect();
      clearTimeout(saveTimer);
      clearTimeout(observerTimer);
      document.removeEventListener("input", onInput, true);
      document.removeEventListener("change", onInput, true);
      document.removeEventListener("click", onInput, true);
      document.removeEventListener("mousedown", flushSave, true);
      window.removeEventListener("popstate", onPop);
      window.removeEventListener("pagehide", persistNow);
      window.removeEventListener("beforeunload", persistNow);
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  return null;
}

export default ModalDraftManager;
